import React, {useEffect, useState} from "react";
import './LabelsExportPopup.css';
import DinA4GridPreview from "./DinA4GridPreview";
import {Document, Page, PDFDownloadLink, Text, View} from "@react-pdf/renderer";
import axios from "axios";
import {API_BASE_URL} from "../constants";

interface LabelsExportPopupProps {
  onClose: () => void;
}

export type HelperLabel = {
  id: number;
  firstName: string;
  surname: string;
  tshirt: string | null;
  totalHours: number;
  createdAt: string;
  fachbereiche: string[];
  hilftNichtAmWettkampftag: boolean;
};

interface HelferpunkteByHelferId {
  [key: number]: Helferpunkt[]
}

interface Helferpunkt {
  id: number;
  einsatzdatum?: string;
  einsatzort: string;
  einsatzuhrzeit: string;
  einsatzuhrzeit_bis?: string | null;
  fachbereich: string;
}

export interface Helper {
  id: number;
  first_name: string;
  surname: string;
  fachbereiche: string[];
  hilft_nicht_am_wettkampftag?: number | null; // 0 = helps on race day, 1 = doesn't help
  tshirt?: string | null;
  created_at: string;
}

const LabelsExportPopup: React.FC<LabelsExportPopupProps> = ({onClose}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState(10);
  const [columns, setColumns] = useState(4);
  const [labels, setLabels] = useState<HelperLabel[]>([]);
  const [createdSinceDate, setCreatedSinceDate] = useState<string>("");
  const [includeHilftNichtAmWettkampftag, setIncludeHilftNichtAmWettkampftag] = useState<boolean>(false);
  const [includeWithoutAssignment, setIncludeWithoutAssignment] = useState<boolean>(false);

  const filteredLabels = labels
    .filter(label => includeWithoutAssignment || !label.hilftNichtAmWettkampftag)
    .filter(label => includeWithoutAssignment || label.totalHours > 0)
    .filter(label => {
      if (!createdSinceDate) return true;

      const helperCreatedAt = new Date(label.createdAt);
      const createdSince = new Date(`${createdSinceDate}T00:00:00`);

      return helperCreatedAt >= createdSince;
    })
  const pageCount = Math.ceil(filteredLabels.length / (rows * columns));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const helpers = await fetchHelpers();
      const assignments = await fetchAssignments();

      const labels: HelperLabel[] = helpers
        .sort((left, right) => left.surname.localeCompare(right.surname))
        .map(helper => {
          const helperAssignments = assignments[helper.id] || [];

          let totalHours = 0;
          helperAssignments.forEach((hp) => {
            if (hp.einsatzuhrzeit && hp.einsatzuhrzeit_bis) {
              totalHours += calculateHours(hp.einsatzuhrzeit, hp.einsatzuhrzeit_bis);
            }
          });

          const fachbereiche = Array.from(new Set(helperAssignments.map(assignment => assignment.fachbereich)))
            .sort((left, right) => left.localeCompare(right));

          return {
            id: helper.id,
            firstName: helper.first_name,
            surname: helper.surname,
            tshirt: helper.tshirt ?? null,
            createdAt: helper.created_at,
            totalHours: Math.ceil(Math.max(0, totalHours)),
            fachbereiche: fachbereiche,
            hilftNichtAmWettkampftag: helper.hilft_nicht_am_wettkampftag !== 0,
          };
        });

      console.log(labels);
      setLabels(labels);
      setLoading(false);
    }

    fetchData();
  }, []);

  // Calculate hours between two time strings (HH:MM format)
  const calculateHours = (timeFrom: string, timeTo: string | null): number => {
    if (!timeFrom || !timeTo) {
      return 0;
    }

    try {
      const [hoursFrom, minutesFrom] = timeFrom.split(':').map(Number);
      const [hoursTo, minutesTo] = timeTo.split(':').map(Number);

      const fromMinutes = hoursFrom * 60 + minutesFrom;
      const toMinutes = hoursTo * 60 + minutesTo;

      // Handle case where timeTo is on the next day
      let diffMinutes = toMinutes - fromMinutes;
      if (diffMinutes < 0) {
        diffMinutes += 24 * 60; // Add 24 hours
      }

      return diffMinutes / 60;
    } catch (error) {
      return 0;
    }
  };

  const fetchHelpers = async (): Promise<Helper[]> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      if (response.data.success) {
        return response.data.users || [];
      }
    } catch (err) {
      console.error('Error fetching helpers:', err);
    }
    return [];
  };

  const fetchAssignments = async (): Promise<HelferpunkteByHelferId> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/helpers/assignments`);
      if (response.data.success) {
        return response.data.assignments || {};
      }
    } catch (err) {
      console.error('Error fetching helper assignments:', err);
    }
    return {};
  };

  return (
    <>
      <div className="labels-export-overlay">
        <div className="labels-export-container">
          <div className="admin-header">
            <h2 className="admin-header-title">Labels für Helfertüten</h2>
            <button className="admin-header-close" onClick={onClose}>×</button>
          </div>

          <div className="labels-export-content">
            {loading && <div>Helfer werden geladen...</div>}

            <div style={{ display: "flex", gap: "12px", width: "100%", alignItems: "stretch" }}>
              <label style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                Zeilen
                <input
                  type="number"
                  min={1}
                  value={rows}
                  onChange={(event) =>
                    setRows(Number(event.target.value))
                  }
                  className="labels-export-input"
                />
              </label>

              <label style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                Spalten
                <input
                  type="number"
                  min={1}
                  value={columns}
                  onChange={(event) =>
                    setColumns(Number(event.target.value))
                  }
                  className="labels-export-input"
                />
              </label>
            </div>

            <div style={{ width: "100%", display: "flex", gap: "12px", alignItems: "stretch" }}>
              <label style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                Helferregistrierung ab
                <input
                  className="labels-export-input"
                  type="date"
                  value={createdSinceDate}
                  onChange={event => setCreatedSinceDate(event.target.value)}
                />
              </label>
            </div>

            <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
              <label className="switch-container">
                <input
                  type="checkbox"
                  className="switch-input"
                  checked={includeHilftNichtAmWettkampftag}
                  onChange={(e) => {
                    setIncludeHilftNichtAmWettkampftag(e.target.checked);
                  }}
                  title="Einschl. hilft nicht am Wettkampftag"
                />
                <span className="switch-track switch-track-red">
                    <span className="switch-thumb" />
                  </span>
              </label>
              <div style={{ flex: 1, fontSize: '0.85rem'}}>Einschl. "hilft nicht am Wettkampftag"</div>
            </div>

            <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
              <label className="switch-container">
                <input
                  type="checkbox"
                  className="switch-input"
                  checked={includeWithoutAssignment}
                  onChange={(e) => {
                    setIncludeWithoutAssignment(e.target.checked);
                  }}
                  title="Einschl. Helfer ohne Zuweisung"
                />
                <span className="switch-track switch-track-red">
                    <span className="switch-thumb" />
                  </span>
              </label>
              <div style={{ flex: 1, fontSize: '0.85rem'}}>Einschl. Helfer ohne Zuweisung</div>
            </div>

            <DinA4GridPreview columns={columns} rows={rows}/>

            <div>
                <b>{filteredLabels.length}</b> Label{filteredLabels.length > 1 ? "s" : ""}, <b>{pageCount}</b> Seite{pageCount > 1 ? "n" : ""}
            </div>

            <PDFDownloadLink
              document={
                <LabelsPdf
                  labels={filteredLabels}
                  rowCount={rows}
                  columnCount={columns}
                />
              }
              fileName={`Roemerman_Helfertueten.pdf`}
              className="labels-export-button"
            >
              PDF herunterladen
            </PDFDownloadLink>
          </div>
        </div>
      </div>
    </>
  );
}

export default LabelsExportPopup;

function LabelsPdf({ labels, rowCount, columnCount}: { labels: HelperLabel[]; rowCount: number; columnCount: number; }) {
  labels = labels.sort((a, b) =>
    a.surname.localeCompare(b.surname) || a.firstName.localeCompare(b.firstName)
  )

  const pdfStyles = {
    container: {
      width: `${100 / columnCount}%`,
      height: `${100 / rowCount}%`,
      padding: 8,
      margin: 0,
      display: "flex" as const,
      flexDirection: "column" as const,
      gap: 8,
    },
    header: {
      display: "flex" as const,
      flexDirection: "row" as const,
      justifyContent: "space-between" as const,
      alignItems: "flex-start" as const
    },
    box: {
      position: "absolute" as const,
      right: 4,
      minWidth: 24,
      paddingHorizontal: 4,
      paddingVertical: 2,
      borderWidth: 1,
      borderColor: "black",
      borderRadius: 4,
      display: "flex" as const,
      alignItems: "center" as const,
      justifyContent: "center" as const,
    },
    boxText: {
      fontSize: 14,
      fontWeight: "bold" as const,
      textAlign: "center" as const,
    },
    surname: {
      fontSize: 14,
      fontWeight: "bold" as const,
    },
    firstName: {
      fontSize: 12,
    },
    fachbereich: {
      fontSize: 10,
      color: "#666666",
    },
  };

  const shirtSizeStyles: Record<string, { backgroundColor: string; color: string }> = {
    XS: {
      backgroundColor: "#2563EB",
      color: "#FFFFFF"
    },
    S: {
      backgroundColor: "#16A34A",
      color: "#FFFFFF"
    },
    M: {
      backgroundColor: "#EAB308",
      color: "#111827"
    },
    L: {
      backgroundColor: "#F97316",
      color: "#FFFFFF"
    },
    XL: {
      backgroundColor: "#DC2626",
      color: "#FFFFFF"
    },
    XXL: {
      backgroundColor: "#9333EA",
      color: "#FFFFFF"
    },
    XXXL: {
      backgroundColor: "#0891B2",
      color: "#FFFFFF"
    }
  };

  function getShirtSizeStyle(size: string | null | undefined) {
    if (!size) return {};

    return shirtSizeStyles[size.toUpperCase()] ?? {};
  }

  function chunk<T>(items: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let index = 0; index < items.length; index += size) {
      chunks.push(items.slice(index, index + size));
    }
    return chunks;
  }

  const labelsPerPage = rowCount * columnCount;

  return (
    <Document>
      {chunk(labels, labelsPerPage).map((pageLabels, pageIndex) => (
        <Page key={pageIndex} size="A4" style={{ padding: 0 }}>
          <View style={{ flexDirection: "row", flexWrap: "wrap", height: "100%" }}>
            {pageLabels.map((label: HelperLabel) => (
              <View key={label.id} style={pdfStyles.container}>
                {label.tshirt && (() =>  {
                  const shirtSizeStyle = getShirtSizeStyle(label.tshirt);
                  return (<View style={[pdfStyles.box, shirtSizeStyle, { top: 4 }]}>
                    <Text style={[pdfStyles.boxText, shirtSizeStyle]}>{label.tshirt}</Text>
                  </View>);
                })()}

                {label.totalHours > 0 && <View style={[pdfStyles.box, { bottom: 4 }]}>
                  <Text style={pdfStyles.boxText}>{label.totalHours}</Text>
                </View>}

                <View>
                  <Text style={pdfStyles.surname}>{label.surname},</Text>
                  <Text style={pdfStyles.firstName}>{label.firstName}</Text>
                </View>

                <View>
                  {label.fachbereiche.map(fachbereich => <Text style={pdfStyles.fachbereich}>{fachbereich}</Text>)}
                </View>
              </View>
            ))}
          </View>
        </Page>
      ))}
    </Document>
  );
}